import { httpsCallable } from "firebase/functions";
import { useFunctions } from "reactfire";
import { useMemo } from "react";
import {
  CreateCustomCenterUserTokenParams,
  CreateCustomCenterUserTokenResponse,
  AddCenterTimeSlotParams,
  AddCenterTimeSlotResponse,
  EditCenterTimeSlotParams,
  EditCenterTimeSlotResponse,
  DeleteCenterTimeSlotParams,
  DeleteCenterTimeSlotResponse,
  UpdateLastUsedRoleParams,
  UpdateLastUsedRoleResponse,
  CancelAppointmentParams,
  CancelAppointmentResponse,
  RescheduleAppointmentParams,
  RescheduleAppointmentResponse,
  CreateAppointmentNoteParams,
  CreateAppointmentNoteResponse,
  UpdateCenterPatientParams,
  UpdateCenterPatientResponse,
  UpdateCenterAddressParams,
  UpdateCenterAddressResponse,
  UpdateCenterContactInformationParams,
  UpdateCenterContactInformationResponse,
  UpdateCenterOperatingHoursParams,
  UpdateCenterOperatingHoursResponse,
  UpdateCenterDiscountParams,
  UpdateCenterDiscountResponse,
  UpdateCenterOfferingParams,
  UpdateCenterOfferingResponse,
  AddCenterOfferingParams,
  AddCenterOfferingResponse,
  AddCenterAddOnParams,
  AddCenterAddOnResponse,
  UpdateCenterAddOnParams,
  UpdateCenterAddOnResponse,
  ArchiveCenterOfferingParams,
  ArchiveCenterOfferingResponse,
  ArchiveCenterAddOnParams,
  ArchiveCenterAddOnResponse,
  AddAppointmentFilesParams,
  AddAppointmentFilesResponse,
  SendCodePatientToUpdateEmailParams,
  SendCodePatientToUpdateEmailResponse,
  AddCenterPackageParams,
  AddCenterPackageResponse,
  UpdateCenterPackageParams,
  UpdateCenterPackageResponse,
  MarkAppointmentOfferingAdditionalViewParams,
  MarkAppointmentOfferingAdditionalViewResponse,
  ArchiveCenterPackageParams,
  ArchiveCenterPackageResponse,
  MarkAppointmentPriorityParams,
  MarkAppointmentPriorityResponse,
  MarkAppointmentAsNoShowParams,
  MarkAppointmentAsNoShowResponse,
  GetCustomerPaymentMethodsResponse,
  GetCustomerPaymentMethodsParams,
  BookAdditionalViewAppointmentParams,
  BookAdditionalViewAppointmentResponse,
  PayAppointmentRemainingBalanceParams,
  PayAppointmentRemainingBalanceResponse,
  UpdateCenterCustomerParams,
  UpdateCenterCustomerResponse,
  SendCodeCustomerToUpdateEmailResponse,
  SendCodeCustomerToUpdateEmailParams,
  ProvideCenterAppAppointmentScreeningFormParams,
  ProvideCenterAppAppointmentScreeningFormResponse,
  GetPatientFormSignedUrlParams,
  GetPatientFormSignedUrlResponse,
  GetAppointmentFormSignedUrlParams,
  GetAppointmentFormSignedUrlResponse,
  ConfirmAppointmentParams,
  ConfirmAppointmentResponse,
  MarkAppointmentOfferingAsReadingParams,
  MarkAppointmentOfferingAsReadingResponse,
  GetCenterAppCustomerAuthCodeParams,
  GetCenterAppCustomerAuthCodeResponse,
  CenterAppCheckInAppointmentParams,
  CenterAppCheckInAppointmentResponse,
  ResendAppointmentReportParams,
  ResendAppointmentReportResponse,
  GenerateReportSignedUrlParams,
  GenerateReportSignedUrlResponse,
  MarkAppointmentReportUnexpectedFindingsParams,
  MarkAppointmentReportUnexpectedFindingsResponse,
  ConfirmAppointmentReportReceiptParams,
  ConfirmAppointmentReportReceiptResponse,
  RegisterCenterInMwlParams,
  RegisterCenterInMwlResponse,
  EditAppointmentTechnologistNoteParams,
  EditAppointmentTechnologistNoteResponse,
  AdjustAppointmentPriceParams,
  AdjustAppointmentPriceResponse,
  AddAppointmentRefundParams,
  AddAppointmentRefundResponse,
  EditAppointmentServicesParams,
  EditAppointmentServicesResponse,
  EditAppointmentAddOnsParams,
  EditAppointmentAddOnsResponse,
  GenerateReceiptSignedUrlParams,
  ResendAppointmentReceiptParams,
  ResendAppointmentReceiptResponse,
  GenerateReceiptSignedUrlResponse,
  StartEditingAIReportParams,
  StartEditingAIReportResponse,
  CancelEditingAIReportParams,
  CancelEditingAIReportResponse,
  ApproveAppointmentAIReportParams,
  ApproveAppointmentAIReportResponse,
  EditRadiologistsCalendarDaysParams,
  EditRadiologistsCalendarDaysResponse,
  SearchCustomersForMergeParams,
  SearchCustomersForMergeResponse,
  MergeCustomersParams,
  MergeCustomersResponse,
  MergePatientParams,
  MergePatientResponse,
  SearchPatientsForMergeParams,
  SearchPatientsForMergeResponse,
} from "@Shape-Digital/kudzu-data/lib/types/actions";

import { FUNCTIONS_PREFIX } from "../common/constants";

type EditAppointmentDurationParams = {
  appointmentId: string;
  durationMinutes: number;
};

type EditAppointmentDurationResponse = void;

type FunctionResource = {
  createCustomCenterUserToken: [
    CreateCustomCenterUserTokenParams,
    CreateCustomCenterUserTokenResponse,
  ];
  addCenterTimeSlot: [AddCenterTimeSlotParams, AddCenterTimeSlotResponse];
  editCenterTimeSlot: [EditCenterTimeSlotParams, EditCenterTimeSlotResponse];
  deleteCenterTimeSlot: [
    DeleteCenterTimeSlotParams,
    DeleteCenterTimeSlotResponse,
  ];
  updateLastUsedRole: [UpdateLastUsedRoleParams, UpdateLastUsedRoleResponse];
  cancelAppointment: [CancelAppointmentParams, CancelAppointmentResponse];
  createAppointmentNote: [
    CreateAppointmentNoteParams,
    CreateAppointmentNoteResponse,
  ];
  rescheduleAppointment: [
    RescheduleAppointmentParams,
    RescheduleAppointmentResponse,
  ];
  updateCenterPatient: [UpdateCenterPatientParams, UpdateCenterPatientResponse];
  updateCenterAddress: [UpdateCenterAddressParams, UpdateCenterAddressResponse];
  updateCenterContactInformation: [
    UpdateCenterContactInformationParams,
    UpdateCenterContactInformationResponse,
  ];
  updateCenterOperatingHours: [
    UpdateCenterOperatingHoursParams,
    UpdateCenterOperatingHoursResponse,
  ];
  updateCenterDiscount: [
    UpdateCenterDiscountParams,
    UpdateCenterDiscountResponse,
  ];
  updateCenterOffering: [
    UpdateCenterOfferingParams,
    UpdateCenterOfferingResponse,
  ];
  addCenterOffering: [AddCenterOfferingParams, AddCenterOfferingResponse];
  addCenterAddOn: [AddCenterAddOnParams, AddCenterAddOnResponse];
  updateCenterAddOn: [UpdateCenterAddOnParams, UpdateCenterAddOnResponse];
  archiveCenterOffering: [
    ArchiveCenterOfferingParams,
    ArchiveCenterOfferingResponse,
  ];
  archiveCenterAddOn: [ArchiveCenterAddOnParams, ArchiveCenterAddOnResponse];
  addAppointmentFiles: [AddAppointmentFilesParams, AddAppointmentFilesResponse];
  sendCodePatientToUpdateEmail: [
    SendCodePatientToUpdateEmailParams,
    SendCodePatientToUpdateEmailResponse,
  ];
  addCenterPackage: [AddCenterPackageParams, AddCenterPackageResponse];
  updateCenterPackage: [UpdateCenterPackageParams, UpdateCenterPackageResponse];
  markAppointmentOfferingAdditionalView: [
    MarkAppointmentOfferingAdditionalViewParams,
    MarkAppointmentOfferingAdditionalViewResponse,
  ];
  archiveCenterPackage: [
    ArchiveCenterPackageParams,
    ArchiveCenterPackageResponse,
  ];
  markAppointmentPriority: [
    MarkAppointmentPriorityParams,
    MarkAppointmentPriorityResponse,
  ];
  markAppointmentAsNoShow: [
    MarkAppointmentAsNoShowParams,
    MarkAppointmentAsNoShowResponse,
  ];
  getCustomerPaymentMethods: [
    GetCustomerPaymentMethodsParams,
    GetCustomerPaymentMethodsResponse,
  ];
  bookAdditionalViewsAppointment: [
    BookAdditionalViewAppointmentParams,
    BookAdditionalViewAppointmentResponse,
  ];
  payAppointmentRemainingBalance: [
    PayAppointmentRemainingBalanceParams,
    PayAppointmentRemainingBalanceResponse,
  ];
  sendCodeCustomerToUpdateEmail: [
    SendCodeCustomerToUpdateEmailParams,
    SendCodeCustomerToUpdateEmailResponse,
  ];
  updateCenterCustomer: [
    UpdateCenterCustomerParams,
    UpdateCenterCustomerResponse,
  ];
  provideCenterAppAppointmentScreeningForm: [
    ProvideCenterAppAppointmentScreeningFormParams,
    ProvideCenterAppAppointmentScreeningFormResponse,
  ];
  getPatientFormSignedUrl: [
    GetPatientFormSignedUrlParams,
    GetPatientFormSignedUrlResponse,
  ];
  getAppointmentFormSignedUrl: [
    GetAppointmentFormSignedUrlParams,
    GetAppointmentFormSignedUrlResponse,
  ];
  markAppointmentOfferingAsReading: [
    MarkAppointmentOfferingAsReadingParams,
    MarkAppointmentOfferingAsReadingResponse,
  ];
  getCenterAppCustomerAuthCode: [
    GetCenterAppCustomerAuthCodeParams,
    GetCenterAppCustomerAuthCodeResponse,
  ];
  confirmAppointment: [ConfirmAppointmentParams, ConfirmAppointmentResponse];
  centerAppCheckInAppointment: [
    CenterAppCheckInAppointmentParams,
    CenterAppCheckInAppointmentResponse,
  ];
  generateReportSignedUrl: [
    GenerateReportSignedUrlParams,
    GenerateReportSignedUrlResponse,
  ];
  resendAppointmentReport: [
    ResendAppointmentReportParams,
    ResendAppointmentReportResponse,
  ];
  markAppointmentReportUnexpectedFindings: [
    MarkAppointmentReportUnexpectedFindingsParams,
    MarkAppointmentReportUnexpectedFindingsResponse,
  ];
  confirmAppointmentReportReceipt: [
    ConfirmAppointmentReportReceiptParams,
    ConfirmAppointmentReportReceiptResponse,
  ];
  registerCenterInMwl: [RegisterCenterInMwlParams, RegisterCenterInMwlResponse];
  editAppointmentTechnologistNote: [
    EditAppointmentTechnologistNoteParams,
    EditAppointmentTechnologistNoteResponse,
  ];
  adjustAppointmentPrice: [
    AdjustAppointmentPriceParams,
    AdjustAppointmentPriceResponse,
  ];
  addAppointmentRefund: [
    AddAppointmentRefundParams,
    AddAppointmentRefundResponse,
  ];
  editAppointmentServices: [
    EditAppointmentServicesParams,
    EditAppointmentServicesResponse,
  ];
  editAppointmentAddOns: [
    EditAppointmentAddOnsParams,
    EditAppointmentAddOnsResponse,
  ];
  resendAppointmentReceipt: [
    ResendAppointmentReceiptParams,
    ResendAppointmentReceiptResponse,
  ];
  generateReceiptSignedUrl: [
    GenerateReceiptSignedUrlParams,
    GenerateReceiptSignedUrlResponse,
  ];
  startEditingAIReport: [
    StartEditingAIReportParams,
    StartEditingAIReportResponse,
  ];
  cancelEditingAIReport: [
    CancelEditingAIReportParams,
    CancelEditingAIReportResponse,
  ];
  approveAppointmentAIReport: [
    ApproveAppointmentAIReportParams,
    ApproveAppointmentAIReportResponse,
  ];
  editAppointmentDuration: [
    EditAppointmentDurationParams,
    EditAppointmentDurationResponse,
  ];
  editRadiologistsCalendarDays: [
    EditRadiologistsCalendarDaysParams,
    EditRadiologistsCalendarDaysResponse,
  ];
  searchCustomersForMerge: [
    SearchCustomersForMergeParams,
    SearchCustomersForMergeResponse,
  ];
  mergeCustomers: [MergeCustomersParams, MergeCustomersResponse];
  mergePatient: [MergePatientParams, MergePatientResponse];
  searchPatientsForMerge: [
    SearchPatientsForMergeParams,
    SearchPatientsForMergeResponse,
  ];
};

// Phase A dispatchers — every current function is grouped here. Kept as a
// Partial (with a direct-call fallback below) so a function added standalone
// in the future, before it's wired into a dispatcher, still works.
const ACTION_GROUP: Partial<Record<keyof FunctionResource, string>> = {
  createCustomCenterUserToken: "auth",
  updateLastUsedRole: "auth",
  getCenterAppCustomerAuthCode: "auth",
  addCenterTimeSlot: "timeslot",
  editCenterTimeSlot: "timeslot",
  deleteCenterTimeSlot: "timeslot",
  cancelAppointment: "appointment",
  rescheduleAppointment: "appointment",
  createAppointmentNote: "appointment",
  markAppointmentPriority: "appointment",
  markAppointmentAsNoShow: "appointment",
  bookAdditionalViewsAppointment: "appointment",
  markAppointmentOfferingAdditionalView: "appointment",
  confirmAppointment: "appointment",
  centerAppCheckInAppointment: "appointment",
  markAppointmentOfferingAsReading: "appointment",
  editAppointmentTechnologistNote: "appointment",
  adjustAppointmentPrice: "appointment",
  editAppointmentDuration: "appointment",
  editRadiologistsCalendarDays: "appointment",
  addAppointmentFiles: "appointmentFiles",
  editAppointmentServices: "appointmentFiles",
  editAppointmentAddOns: "appointmentFiles",
  getPatientFormSignedUrl: "appointmentFiles",
  provideCenterAppAppointmentScreeningForm: "appointmentFiles",
  getAppointmentFormSignedUrl: "appointmentFiles",
  addCenterOffering: "offering",
  updateCenterOffering: "offering",
  archiveCenterOffering: "offering",
  addCenterAddOn: "offering",
  updateCenterAddOn: "offering",
  archiveCenterAddOn: "offering",
  addCenterPackage: "offering",
  updateCenterPackage: "offering",
  archiveCenterPackage: "offering",
  updateCenterAddress: "center",
  updateCenterContactInformation: "center",
  updateCenterOperatingHours: "center",
  updateCenterDiscount: "center",
  registerCenterInMwl: "center",
  updateCenterPatient: "patientCustomer",
  updateCenterCustomer: "patientCustomer",
  sendCodePatientToUpdateEmail: "patientCustomer",
  sendCodeCustomerToUpdateEmail: "patientCustomer",
  getCustomerPaymentMethods: "payment",
  payAppointmentRemainingBalance: "payment",
  addAppointmentRefund: "payment",
  generateReportSignedUrl: "report",
  resendAppointmentReport: "report",
  markAppointmentReportUnexpectedFindings: "report",
  confirmAppointmentReportReceipt: "report",
  startEditingAIReport: "report",
  cancelEditingAIReport: "report",
  approveAppointmentAIReport: "report",
  resendAppointmentReceipt: "receipt",
  generateReceiptSignedUrl: "receipt",
  searchCustomersForMerge: "patientCustomer",
  mergeCustomers: "patientCustomer",
  mergePatient: "patientCustomer",
  searchPatientsForMerge: "patientCustomer",
};

type FunctionReq<K extends keyof FunctionResource> = FunctionResource[K][0];
type FunctionRes<K extends keyof FunctionResource> = FunctionResource[K][1];

const useFirebaseAppFunction = <K extends keyof FunctionResource>(
  functionName: K,
) => {
  const functions = useFunctions();

  const func = useMemo(() => {
    const group = ACTION_GROUP[functionName];

    if (!group) {
      return httpsCallable<FunctionReq<K>, FunctionRes<K>>(
        functions,
        `${FUNCTIONS_PREFIX}-${functionName}`,
      );
    }

    const callable = httpsCallable<
      { action: string; payload: FunctionReq<K> },
      FunctionRes<K>
    >(functions, `${FUNCTIONS_PREFIX}-${group}`);
    return (params: FunctionReq<K>) =>
      callable({ action: functionName as string, payload: params });
  }, [functionName, functions]);

  return func;
};

export default useFirebaseAppFunction;
